// การ์ดสัตว์ (docs/PLAN.md ข้อ 2)

import { IUCN_TH, THREATENED } from "../../data/iucn";
import { DIET_TH, HABITAT_TH, formatAge } from "../../engine/text";
import { stagesOf } from "../../engine/life";
import { startTrack } from "../../geo/locate";
import { neighborsAt } from "../../geo/neighbors";
import type { SpeciesRange } from "../../geo/range";
import type { SpawnResult } from "../../shared/api";
import { GeoPanel } from "../components/GeoPanel";
import type { GeoState } from "../geo";
import { sourcesAt } from "../neighbors";
import { countryName, fmt, groupNames } from "../names";

interface Props {
  spawn: SpawnResult;
  geo: GeoState;
  range: SpeciesRange | null;
  onStart: () => void;
  onBack: () => void;
}

export function SpeciesCardView({ spawn, geo, range, onStart, onBack }: Props) {
  const { species } = spawn;
  const { group, subgroup } = groupNames(species.subgroupId);
  const t = species.traits;
  const track = geo.status === "ready" && range ? startTrack(species, spawn.seed, range, geo.data.grid) : null;
  // บนการ์ดบอกนักล่าและอาหารของตัวเต็มวัยแถวจุดเกิด
  const adult = stagesOf(species).find((s) => s.tag === "adult")!;
  const neighbors = track ? neighborsAt(species, adult, track, { sources: sourcesAt(species, adult, track.cell) }) : undefined;
  const young = t.reproduction === "eggs" ? `วางไข่ครั้งละประมาณ ${fmt(t.clutchSize)} ฟอง` : `ออกลูกครั้งละประมาณ ${fmt(t.clutchSize)} ตัว`;

  return (
    <main className="screen">
      <section className="card">
        <h1 className="species-name">{species.nameTh ?? <span className="muted">ยังไม่มีชื่อไทย</span>}</h1>
        <p className="sci">{species.sci}</p>
        {species.iucn && (
          <p style={{ marginTop: 8 }}>
            <span className={THREATENED.has(species.iucn) ? "badge warn" : "badge"}>
              {IUCN_TH[species.iucn]} ({species.iucn})
            </span>
          </p>
        )}
      </section>

      <section className="card">
        <dl className="facts">
          <dt>กลุ่ม</dt>
          <dd>{group}</dd>
          <dt>กลุ่มย่อย</dt>
          <dd>{subgroup}</dd>
          {spawn.className && (
            <>
              <dt>ชั้น</dt>
              <dd className="sci">{spawn.className}</dd>
            </>
          )}
          {spawn.order && (
            <>
              <dt>อันดับ</dt>
              <dd className="sci">{spawn.order}</dd>
            </>
          )}
          {spawn.family && (
            <>
              <dt>วงศ์</dt>
              <dd className="sci">{spawn.family}</dd>
            </>
          )}
          <dt>อายุขัย</dt>
          <dd>ประมาณ {formatAge(t.lifespanYears * 365)}</dd>
          <dt>ลูก</dt>
          <dd>{young}</dd>
          <dt>อาหาร</dt>
          <dd>{DIET_TH[t.diet]}</dd>
          <dt>ถิ่นที่อยู่</dt>
          <dd>{t.habitat.map((h) => HABITAT_TH[h]).join(" · ")}</dd>
          {spawn.countries.length > 0 && (
            <>
              <dt>พบใน</dt>
              <dd>{spawn.countries.slice(0, 6).map(countryName).join(" · ")}{spawn.countries.length > 6 ? " …" : ""}</dd>
            </>
          )}
        </dl>
        {spawn.traitsEstimated && <p className="small muted" style={{ marginTop: 8 }}>อายุขัยและจำนวนลูกเป็นค่าประมาณจากกลุ่มย่อย</p>}
      </section>

      <GeoPanel
        geo={geo}
        card={spawn}
        range={range}
        track={track}
        mode="birth"
        neighbors={neighbors}
      />

      {spawn.oneIn !== null && (
        <p className="odds">โอกาสที่จะเกิดเป็นสัตว์ชนิดนี้: 1 ใน {fmt(spawn.oneIn)}</p>
      )}
      {spawn.source === "demo" && (
        <p className="notice">
          โหมดตัวอย่าง: ฐานข้อมูลยังนำเข้าสัตว์ไม่เสร็จ ตอนนี้สุ่มจากสัตว์ตัวอย่าง{spawn.poolSize ? ` ${spawn.poolSize} ชนิด` : "ที่คัดไว้"}
        </p>
      )}

      <div className="actions">
        <button className="btn primary block" onClick={onStart}>
          เริ่มชีวิต
        </button>
        <button className="btn block" onClick={onBack}>
          กลับหน้าแรก
        </button>
      </div>
    </main>
  );
}
